import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { NavegationPagesService } from 'src/app/services/navegation-pages.service';
import { ThemeServiceService } from 'src/app/services/theme-service.service';
import { MenuConfigService } from 'src/app/services/menu-config.service';
import { MenuItem } from 'src/app/config/menu-config';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  private stop$ = new Subject<void>();
  public currentUrl: string = '/';
  public showMenu: boolean = false;
  public estadoTheme: string = 'dark';
  public img: string = '../../assets/img/logo2-black.png';
  public seleccionado: string = 'home';
  public isMonochromeMode: boolean = false;

  public itemsMenu: Array<{
    nombre: string;
    icono: string;
    ruta: string;
    id: string;
    enabled?: boolean;
  }> = [];

  // Mapeo de iconos para los menús
  private iconMapping: { [key: string]: string } = {
    'home': 'iconsminds-shop-4',
    'abautMe': 'iconsminds-administrator',
    'skills': 'iconsminds-administrator',
    'experience': 'iconsminds-file-clipboard-file---text',
    'projects': 'iconsminds-handshake',
    'gallery': 'iconsminds-suitcase',
    'clients': 'iconsminds-conference',
    'contact': 'iconsminds-smartphone-4'
  };

  // Mapeo de rutas del sistema actual
  private routeMapping: { [key: string]: string } = {
    'home': '',
    'abautMe': 'abautMe',
    'skills': 'skills',
    'experience': 'experience',
    'projects': 'projects',
    'gallery': 'gallery',
    'clients': 'clients',
    'contact': 'contact'
  };

  constructor(
    private readonly _navegationPageService: NavegationPagesService,
    private readonly _themeService: ThemeServiceService,
    private readonly _menuConfigService: MenuConfigService,
    private readonly _router: Router
  ) {}

  ngOnInit(): void {
    this.loadMenuItems();
    this.currentUrl = this._router.url;

    this._router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.stop$)
      )
      .subscribe((event) => {
        this.currentUrl = event.urlAfterRedirects || event.url;
      });
    
    // Escuchar cambios en la configuración de menús
    this._menuConfigService.onConfigChange()
      .pipe(takeUntil(this.stop$))
      .subscribe(() => {
        this.loadMenuItems();
      });
  }

  /**
   * Carga los elementos del menú desde la configuración
   */
  private loadMenuItems(): void {
    const enabledMenus = this._menuConfigService.getPrimaryMenus();
    
    this.itemsMenu = enabledMenus.map(menu => ({
      nombre: menu.label,
      icono: this.iconMapping[menu.id] || 'iconsminds-shop-4',
      ruta: this.routeMapping[menu.id] || menu.id,
      id: menu.id,
      enabled: menu.enabled
    }));
  }

  /**
   * Verifica si un menú está habilitado
   */
  isMenuEnabled(menuId: string): boolean {
    return this._menuConfigService.isMenuEnabled(menuId);
  }

  /**
   * Obtiene el número total de menús habilitados
   */
  getEnabledMenuCount(): number {
    return this._menuConfigService.getEnabledMenuCount();
  }
  ngOnDestroy(): void {
    this.stop();
  }

  changeEstadoNav(pagina: string): void {
    this.showMenu = false;
    this.seleccionado = pagina;
    this._navegationPageService.navegationPages(pagina);
  }

  openAndCloseMenu(): void {
    this.showMenu = !this.showMenu;
  }

  closeMenu(): void {
    this.showMenu = false;
  }

  onMenuNavigate(route: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const targetRoute = route ? `/${route}` : '/';
    this.showMenu = false;

    if (this.currentUrl === targetRoute) {
      this.resetMainScroll();
      return;
    }

    this._router.navigateByUrl(targetRoute).then(() => {
      requestAnimationFrame(() => this.resetMainScroll());
      setTimeout(() => this.resetMainScroll(), 720);
    });
  }

  isRouteActive(route: string): boolean {
    const targetRoute = route ? `/${route}` : '/';

    if (targetRoute === '/') {
      return this.currentUrl === '/';
    }

    return this.currentUrl === targetRoute || this.currentUrl.startsWith(`${targetRoute}/`);
  }

  private resetMainScroll(): void {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    const selectors = ['.glow-container', '#content', '.content_main', '.todo'];
    selectors.forEach((selector) => {
      const element = document.querySelector(selector) as HTMLElement | null;
      if (element) {
        element.scrollTop = 0;
      }
    });
  }

  changeTheme(): void {
    if (this.estadoTheme === 'inicial') {
      this.estadoTheme = 'dark';
      this._themeService.setDarkTheme();
    } else {
      this.estadoTheme = 'inicial';
      this._themeService.setDefaultTheme();
    }
  }

  /**
   * Alterna entre modo azul y monocromático
   */
  toggleColorMode(): void {
    this._themeService.toggleColorMode();
    const activeTheme = this._themeService.getActiveTheme();
    this.isMonochromeMode = activeTheme?.name === 'monochrome';
  }

  /**
   * Habilita o deshabilita un menú (método para uso administrativo)
   */
  toggleMenu(menuId: string, enabled: boolean): void {
    this._menuConfigService.toggleMenuEnabled(menuId, enabled);
  }

  /**
   * Actualiza el orden de un menú (método para uso administrativo)
   */
  updateMenuOrder(menuId: string, newOrder: number): void {
    this._menuConfigService.updateMenuOrder(menuId, newOrder);
  }

  /**
   * Exporta la configuración actual (método para uso administrativo)
   */
  exportMenuConfig(): string {
    return this._menuConfigService.exportConfig();
  }

  /**
   * Restaura la configuración por defecto (método para uso administrativo)
   */
  resetMenuConfig(): void {
    this._menuConfigService.resetToDefaultConfig();
  }

  stop(): void {
    this.stop$.next();
    this.stop$.complete();
  }
}
